import {
  Application,
  Container,
  Graphics,
  Sprite,
  Texture,
  TilingSprite,
} from 'pixi.js';
import { BloomFilter } from '@pixi/filter-bloom';
import { TOKENS } from './tokens';
import { GameClock } from './timing';

export interface StageLayerSet {
  bg: Container;
  grid: Container;
  paths: Container;
  notes: Container;
  fx: Container;
  hud: Container;
  /**
   * Re-render static background elements for the new stage size.
   */
  resize: (width: number, height: number) => void;
  /**
   * Enable or disable the scanline overlay.
   */
  setScanlines: (enabled: boolean) => void;
  /**
   * Set or clear a looping background video.
   */
  setBackgroundVideo: (video: HTMLVideoElement | null) => void;
}

/**
 * Initialize standard stage layering and basic background visuals.
 * Creates containers for common layer groups and attaches them to the stage.
 * Also renders the background gradient, vignette, and tiled grid with a
 * subtle parallax animation synced to the {@link GameClock}.
 */
export default function StageLayers(
  app: Application,
  { beatPulse = false }: { beatPulse?: boolean } = {},
): StageLayerSet {
  const stage = app.stage;
  const bg = new Container();
  const grid = new Container();
  const paths = new Container();
  const notes = new Container();
  const fx = new Container();
  const hud = new Container();
  stage.addChild(bg, grid, paths, notes, fx, hud);

  const bloom = new BloomFilter({ threshold: 0.2, brightness: 1.2 });
  notes.filters = [bloom];
  fx.filters = [bloom];

  const { width, height } = app.screen;
  // Center scaling pivot so beat pulses zoom from the middle of the screen
  notes.pivot.set(width / 2, height / 2);
  notes.position.set(width / 2, height / 2);

  // Background gradient elements reused on resize
  const gradCanvas = document.createElement('canvas');
  const gradSprite = Sprite.from(gradCanvas);
  bg.addChild(gradSprite);

  let videoSprite: Sprite | null = null;

  const frame = new Graphics();
  bg.addChild(frame);

  // Vignette overlay
  const vignetteCanvas = document.createElement('canvas');
  const vignetteSprite = Sprite.from(vignetteCanvas);
  bg.addChild(vignetteSprite);

  const drawStatic = (w: number, h: number) => {
    // Gradient
    gradCanvas.width = w;
    gradCanvas.height = h;
    const gctx = gradCanvas.getContext('2d')!;
    const grad = gctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, TOKENS.bg0);
    grad.addColorStop(1, TOKENS.bg1);
    gctx.fillStyle = grad;
    gctx.fillRect(0, 0, w, h);
    gradSprite.texture.update();
    gradSprite.width = w;
    gradSprite.height = h;

    // Frame
    frame.clear();
    frame
      .rect(1, 1, w - 2, h - 2)
      .stroke({
        width: 1,
        color: parseInt(TOKENS.grid.slice(1), 16),
        alpha: 0.4,
      });

    // Vignette
    vignetteCanvas.width = w;
    vignetteCanvas.height = h;
    const vctx = vignetteCanvas.getContext('2d')!;
    const vgrad = vctx.createRadialGradient(
      w / 2,
      h / 2,
      0,
      w / 2,
      h / 2,
      Math.max(w, h) / 2,
    );
    vgrad.addColorStop(0, 'rgba(0,0,0,0)');
    vgrad.addColorStop(1, 'rgba(0,0,0,0.5)');
    vctx.fillStyle = vgrad;
    vctx.fillRect(0, 0, w, h);
    vignetteSprite.texture.update();
    vignetteSprite.width = w;
    vignetteSprite.height = h;
  };

  drawStatic(width, height);

  // Grid texture
  const tileSize = 48;
  const gridCanvas = document.createElement('canvas');
  gridCanvas.width = tileSize;
  gridCanvas.height = tileSize;
  const gridCtx = gridCanvas.getContext('2d')!;
  gridCtx.strokeStyle = TOKENS.grid;
  gridCtx.lineWidth = 1;
  gridCtx.beginPath();
  gridCtx.moveTo(0.5, 0);
  gridCtx.lineTo(0.5, tileSize);
  gridCtx.moveTo(0, 0.5);
  gridCtx.lineTo(tileSize, 0.5);
  gridCtx.stroke();
  const gridTexture = Texture.from(gridCanvas);
  const gridSprite = new TilingSprite({ texture: gridTexture, width, height });
  gridSprite.alpha = 0.07; // ~7% opacity
  grid.addChild(gridSprite);

  // Scanline overlay texture
  const scanCanvas = document.createElement('canvas');
  scanCanvas.width = 1;
  scanCanvas.height = 2;
  const scanCtx = scanCanvas.getContext('2d')!;
  scanCtx.fillStyle = 'rgba(0,0,0,0.03)';
  scanCtx.fillRect(0, 0, 1, 1);
  const scanTexture = Texture.from(scanCanvas);
  const scanSprite = new TilingSprite({ texture: scanTexture, width, height });
  scanSprite.visible = false;
  stage.addChildAt(scanSprite, stage.getChildIndex(notes));

  // Grid parallax animation synced to GameClock beats
  const bpm = 120; // fallback BPM if no song data is present
  const beatMs = 60000 / bpm;
  let lastBeat = -1;
  let dir = 1;
  const easeOutQuad = (t: number) => 1 - (1 - t) * (1 - t);

  app.ticker.add(() => {
    const now = GameClock.now();
    const beat = Math.floor(now / beatMs);
    if (beat !== lastBeat) {
      lastBeat = beat;
      dir *= -1;
      if (beatPulse) {
        for (const child of notes.children) {
          (child as any).pulse?.();
        }
      }
    }
    const phase = (now % beatMs) / beatMs;
    const offset = dir * easeOutQuad(1 - phase) * 4; // ±4px
    gridSprite.tilePosition.set(offset, offset);
  });

  const resize = (w: number, h: number) => {
    drawStatic(w, h);
    gridSprite.width = w;
    gridSprite.height = h;
    scanSprite.width = w;
    scanSprite.height = h;
    notes.pivot.set(w / 2, h / 2);
    notes.position.set(w / 2, h / 2);
    if (videoSprite) {
      videoSprite.width = w;
      videoSprite.height = h;
    }
  };

  const setScanlines = (enabled: boolean) => {
    scanSprite.visible = enabled;
  };

  const setBackgroundVideo = (video: HTMLVideoElement | null) => {
    if (videoSprite) {
      videoSprite.destroy();
      videoSprite = null;
    }
    if (video) {
      const texture = Texture.from(video);
      videoSprite = new Sprite(texture);
      videoSprite.width = app.screen.width;
      videoSprite.height = app.screen.height;
      bg.addChildAt(videoSprite, 0);
      gradSprite.visible = false;
    } else {
      gradSprite.visible = true;
    }
  };

  return { bg, grid, paths, notes, fx, hud, resize, setScanlines, setBackgroundVideo };
}

